import { useCallback, useEffect, useRef, useState } from "react";

import { type DebugSendBody, sendDebugMessage } from "./debug-send-api";
import type { MessageSummary } from "./messages-api";

export type DebugSendStatus = "idle" | "sending" | "polling" | "found" | "timed-out" | "error";

export interface UseDebugSendResult {
  status: DebugSendStatus;
  foundMessageId: string | null;
  errorCode: number | null;
  errorMessage: string | null;
  send: (body: DebugSendBody) => Promise<void>;
  reset: () => void;
}

interface UseDebugSendOptions {
  baseUrl: string;
  namespaceId: string;
  items: MessageSummary[];
  refresh: () => Promise<void>;
}

const POLL_INTERVAL_MS = 2_000;
const POLL_TIMEOUT_MS = 60_000;

export function useDebugSend({
  baseUrl,
  namespaceId,
  items,
  refresh,
}: UseDebugSendOptions): UseDebugSendResult {
  const [status, setStatus] = useState<DebugSendStatus>("idle");
  const [foundMessageId, setFoundMessageId] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const targetSesIdRef = useRef<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number | null>(null);

  const stopPolling = useCallback((): void => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const reset = useCallback((): void => {
    stopPolling();
    targetSesIdRef.current = null;
    pollStartRef.current = null;
    setStatus("idle");
    setFoundMessageId(null);
    setErrorCode(null);
    setErrorMessage(null);
  }, [stopPolling]);

  useEffect(() => {
    if (status !== "polling") return;
    const target = targetSesIdRef.current;
    if (!target) return;

    const match = items.find((m) => m.sesMessageId === target);
    if (match) {
      stopPolling();
      setFoundMessageId(match.id);
      setStatus("found");
    }
  }, [items, status, stopPolling]);

  const send = useCallback(
    async (body: DebugSendBody): Promise<void> => {
      setStatus("sending");
      setErrorCode(null);
      setErrorMessage(null);

      const result = await sendDebugMessage(baseUrl, namespaceId, body);
      const messageId = "messageId" in result ? result.messageId : undefined;

      if (result.status !== 200 || messageId === undefined) {
        setErrorCode(result.status);
        setErrorMessage("errorMessage" in result ? (result.errorMessage ?? null) : null);
        setStatus("error");
        return;
      }

      targetSesIdRef.current = messageId;
      pollStartRef.current = Date.now();
      setStatus("polling");

      await refresh();

      // Defensive: clear any stray interval from a previous `send` before
      // installing a new one, so re-entrant callers can never leak intervals.
      stopPolling();
      intervalRef.current = setInterval((): void => {
        const elapsed = Date.now() - (pollStartRef.current ?? 0);
        if (elapsed >= POLL_TIMEOUT_MS) {
          stopPolling();
          setStatus("timed-out");
          return;
        }
        void refresh();
      }, POLL_INTERVAL_MS);
    },
    [baseUrl, namespaceId, refresh, stopPolling]
  );

  useEffect(() => {
    return (): void => {
      stopPolling();
    };
  }, [stopPolling]);

  return { status, foundMessageId, errorCode, errorMessage, send, reset };
}
